import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertTriangle, ArrowRight } from 'lucide-react';

// Blok 84 — mask an email locally so we never render PII verbatim on
// the success page (shoulder-surfing, screenshots, cached HTML, etc.).
// Mirrors the backend `mask_email` helper. Never used for comparison.
export function maskEmail(addr) {
  if (!addr || typeof addr !== 'string' || !addr.includes('@')) return '';
  const [local, domain] = addr.split('@');
  if (local.length <= 2) return `${local[0] || ''}*@${domain}`;
  return `${local[0]}${'*'.repeat(local.length - 2)}${local[local.length - 1]}@${domain}`;
}

// P0 Fix 2 — Google Ads conversion firing on a confirmed €9,95 monitoring
// payment. Pure client-side, idempotent per Stripe session_id.
//
// Required env (frontend/.env, surfaced via REACT_APP_ prefix):
//   REACT_APP_GOOGLE_ADS_EC_CONVERSION_SEND_TO
//     e.g. "AW-18091985325/<NEW_EC_INTRO_LABEL>"
//
// If the env value is missing or empty:
//   - We do NOT fire window.gtag.
//   - We log a single console.warn with the exact env name + Ads UI step.
//   - The success page still renders normally; klantreis is niet geblokkeerd.
//
// We never send PII (no email, phone, address). Only:
//   send_to, transaction_id (Stripe session_id), value=9.95, currency=EUR.
//
// Dedupe: sessionStorage key `ps_ads_conversion_fired_<sessionId>`.
// Reload of /monitoring/success?session_id=... will NOT refire.
export const ADS_CONVERSION_ENV = 'REACT_APP_GOOGLE_ADS_EC_CONVERSION_SEND_TO';
export const ADS_CONVERSION_GUARD_PREFIX = 'ps_ads_conversion_fired_';
const _warnedMissingConfig = { value: false };

export function _fireGoogleAdsConversion(sessionId, { gtag, sessionStorage: storage, console: cons } = {}) {
  // Wrapper to allow tests to inject mocks. Default to globals.
  const _gtag = gtag !== undefined ? gtag : (typeof window !== 'undefined' ? window.gtag : undefined);
  const _storage = storage !== undefined ? storage : (typeof window !== 'undefined' ? window.sessionStorage : undefined);
  const _console = cons || console;

  if (!sessionId) return { fired: false, reason: 'no_session_id' };

  const sendTo = process.env.REACT_APP_GOOGLE_ADS_EC_CONVERSION_SEND_TO || '';
  if (!sendTo) {
    if (!_warnedMissingConfig.value) {
      _warnedMissingConfig.value = true;
      _console.warn(
        `[Powersure Ads] ${ADS_CONVERSION_ENV} ontbreekt. Geen Google Ads conversion gefired. ` +
        `Stap: maak nieuwe conversie 'Powersure_EC_Intro_Payment' aan in Google Ads UI ` +
        `(AW-18091985325/<NEW_LABEL>), zet als Primary, en plaats de send_to-waarde in ` +
        `frontend/.env onder ${ADS_CONVERSION_ENV}.`
      );
    }
    return { fired: false, reason: 'missing_config' };
  }

  if (typeof _gtag !== 'function') {
    return { fired: false, reason: 'gtag_not_loaded' };
  }

  const guardKey = `${ADS_CONVERSION_GUARD_PREFIX}${sessionId}`;
  try {
    if (_storage && _storage.getItem(guardKey)) {
      return { fired: false, reason: 'already_fired' };
    }
  } catch {
    // storage may be unavailable — proceed with fire but no dedupe
  }

  try {
    _gtag('event', 'conversion', {
      send_to: sendTo,
      transaction_id: sessionId,  // Stripe cs_live_... — dedup at Ads side too
      value: 9.95,
      currency: 'EUR',
    });
    try { _storage && _storage.setItem(guardKey, '1'); } catch { /* ignore */ }
    return { fired: true, reason: 'ok', send_to: sendTo };
  } catch (err) {
    return { fired: false, reason: 'gtag_threw', error: String(err).slice(0, 200) };
  }
}

export default function MonitoringSuccessPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('loading'); // loading | active | pending | failed
  const [data, setData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const pollingRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    let cancelled = false;
    let tries = 0;

    const poll = async () => {
      tries += 1;
      setAttempts(tries);
      try {
        const r = await fetch(`/api/subscriptions/monitoring/status/${encodeURIComponent(sessionId)}`);
        if (!r.ok) {
          if (tries >= 7) {
            if (!cancelled) setStatus('failed');
            return;
          }
        } else {
          const body = await r.json();
          if (cancelled) return;
          setData(body);
          if (body.monitoring_status === 'active' || body.payment_status === 'paid') {
            setStatus('active');
            return;
          }
          if (body.status === 'expired') {
            setStatus('failed');
            return;
          }
        }
      } catch {
        // transient — keep polling
      }
      if (tries >= 7) {
        if (!cancelled) setStatus('pending');
        return;
      }
      pollingRef.current = setTimeout(poll, 2000);
    };

    poll();
    return () => {
      cancelled = true;
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, [sessionId]);

  // P0 Fix 2 — fire Google Ads conversion ONCE, ONLY when the monitoring
  // subscription is confirmed active. Never on pending/failed/loading.
  // Dedupe is per Stripe session_id via sessionStorage.
  useEffect(() => {
    if (status !== 'active') return;
    if (!sessionId) return;
    if (!data) return;
    // Defensive: even if status flipped, require payment_status==='paid'
    // or monitoring_status==='active' from the API response before firing.
    const paid = data.payment_status === 'paid' || data.monitoring_status === 'active';
    if (!paid) return;
    _fireGoogleAdsConversion(sessionId);
  }, [status, sessionId, data]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-white flex items-center justify-center p-4">
      <div
        className="bg-white border border-border rounded-2xl shadow-lg max-w-lg w-full p-8"
        data-testid="monitoring-success-page"
      >
        {status === 'loading' && (
          <div className="text-center space-y-4" data-testid="monitoring-success-loading">
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-primary" />
            <h1 className="text-xl font-semibold text-foreground">Uw betaling wordt verwerkt…</h1>
            <p className="text-sm text-muted-foreground">
              Dit duurt meestal slechts enkele seconden. Sluit deze pagina niet af.
            </p>
            <p className="text-xs text-muted-foreground">Poging {attempts}/7</p>
          </div>
        )}

        {status === 'active' && (
          <div className="text-center space-y-4" data-testid="monitoring-success-active">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-semibold text-foreground" data-testid="monitoring-success-h1">
              Betaling gelukt
            </h1>
            <p className="text-base text-emerald-700 font-medium" data-testid="monitoring-success-activated">
              Energy Control is geactiveerd
            </p>

            {/* Blok 84 — heldere stappen na betaling, met gemaskeerd email */}
            <ol className="text-left bg-emerald-50/60 border border-emerald-200 rounded-lg p-4 space-y-2 text-sm leading-relaxed"
              data-testid="monitoring-success-steps">
              <li className="flex gap-2">
                <span className="font-semibold text-emerald-700">1.</span>
                <span><strong>Betaling gelukt</strong> — Energy Control staat actief op uw account.</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold text-emerald-700">2.</span>
                <span>We sturen uw accountmail
                  {data?.email ? <> naar <strong data-testid="monitoring-success-email-masked">{maskEmail(data.email)}</strong></> : ''}.
                  Aanlevering duurt meestal enkele minuten.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold text-emerald-700">3.</span>
                <span>Open de mail en kies <strong>&quot;Toegang instellen&quot;</strong> om uw wachtwoord aan te maken.</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold text-emerald-700">4.</span>
                <span>Daarna kunt u Mijn Powersure openen — en optioneel <strong>als app installeren</strong> via uw browser-menu.</span>
              </li>
            </ol>

            <div className="space-y-2 pt-2">
              <a
                href="/customer/login"
                className="w-full inline-flex items-center justify-center gap-1 px-4 py-3 rounded-md bg-primary text-white text-sm font-semibold hover:bg-primary/90"
                data-testid="monitoring-success-login-btn"
              >
                Naar inloggen <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="/customer/resend-access"
                className="w-full inline-flex items-center justify-center gap-1 px-4 py-3 rounded-md border-2 border-primary/30 text-primary text-sm font-semibold hover:bg-primary/5"
                data-testid="monitoring-success-resend-btn"
              >
                Ik heb de mail niet ontvangen
              </a>
              <a
                href="/"
                className="w-full inline-flex items-center justify-center gap-1 px-4 py-3 rounded-md border border-border text-foreground text-sm font-semibold hover:bg-muted"
                data-testid="monitoring-success-home-btn"
              >
                Terug naar home
              </a>
            </div>
            <p className="text-[11px] text-muted-foreground pt-2 leading-relaxed">
              Maandelijks opzegbaar via de Stripe-link in uw bevestigingsmail.
            </p>
            <div className="text-[11px] text-muted-foreground bg-muted/40 rounded-lg p-3 border border-border mt-3">
              Geen besparingsgarantie · geen automatische sturing zonder uw OK.
            </div>
          </div>
        )}

        {status === 'pending' && (
          <div className="text-center space-y-4" data-testid="monitoring-success-pending">
            <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
            </div>
            <h1 className="text-xl font-semibold text-foreground">Betaling wordt verwerkt…</h1>
            <p className="text-sm text-muted-foreground">
              De bevestiging van Stripe duurt iets langer dan normaal. U ontvangt een e-mail zodra de
              monitoring actief is. U kunt deze pagina sluiten.
            </p>
          </div>
        )}

        {status === 'failed' && (
          <div className="text-center space-y-4" data-testid="monitoring-success-failed">
            <div className="w-16 h-16 mx-auto rounded-full bg-rose-100 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-rose-600" />
            </div>
            <h1 className="text-xl font-semibold text-foreground">Bevestiging niet gevonden</h1>
            <p className="text-sm text-muted-foreground">
              We konden uw subscription niet direct bevestigen. Als u wel betaald heeft, wordt de
              monitoring nog automatisch geactiveerd zodra Stripe de bevestiging stuurt. U kunt ook
              contact opnemen via het adviesformulier.
            </p>
            <a
              href="/"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-md border border-border text-sm font-semibold hover:bg-muted"
            >
              Terug naar home
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
